/**
 * Everything the grid derives from the store on each render, in one place so the components
 * stay declarative and the arithmetic stays unit-testable (spec M1-B4 §6).
 */
import type { ColumnId, GroupId, ItemId } from '@elizmonday/shared';
import { columnWidth, getColumnUi } from '../columns/registry';
import type { BoardColumn, BoardViewPayload, ItemRow } from '../data/types';
import {
  flatten,
  layoutColumns,
  nameCellWidth,
  offsets,
  totalHeight,
  type ColumnLayout,
  type FlatRow,
} from './layout';
import type { GridGeometry } from './selection';
import { visibleColumns, effectiveRowHeight, type TableViewSettings } from './settings';
import { NAME_COLUMN_ID } from './store';

export interface DerivedGrid {
  readonly pinned: BoardColumn[];
  readonly scrolling: BoardColumn[];
  readonly visible: BoardColumn[];
  readonly layout: ColumnLayout[];
  readonly scrollWidth: number;
  readonly nameColumn: BoardColumn | undefined;
  /** The name **cell**: `nameStickyWidth` less the bar, the rail and the chat cell (P-021). */
  readonly nameWidth: number;
  /** What monday stores in `name.width` — the whole sticky region from the grid's edge. */
  readonly nameStickyWidth: number;
  readonly rows: FlatRow[];
  readonly tops: number[];
  readonly height: number;
  readonly rowHeight: number;
  readonly rowsByGroup: Map<string, ItemRow[]>;
  readonly itemsById: Map<string, ItemRow>;
  readonly geometry: GridGeometry;
  /** Visible order of every loaded row — what `Shift`+click on a checkbox measures against. */
  readonly itemOrder: ItemId[];
  /** M3.S1: the subitems board's columns, and the nested rows keyed by parent. */
  readonly subitemColumns: readonly BoardColumn[];
  readonly subitemsByParent: Record<string, readonly ItemRow[]>;
  /** P-308: the parents that carry the muted "subitems unavailable" line, and its sentence. */
  readonly subitemsUnavailableFor: ReadonlySet<ItemId>;
  readonly subitemsError: string | null;
}

export interface SubitemsState {
  /** Parents whose nested table is open (M3.S1 §6). */
  readonly expandedItemIds: readonly ItemId[];
  readonly byParent: Record<string, readonly ItemRow[]>;
  readonly columns: readonly BoardColumn[];
  /** P-308: parents whose subitems read was refused; each carries one muted line. */
  readonly unavailableFor?: readonly ItemId[];
  /** P-308: the api's own sentence, shown on hover over that line. */
  readonly error?: string | null;
}

const NO_SUBITEMS: SubitemsState = { expandedItemIds: [], byParent: {}, columns: [] };

export function deriveGrid(
  payload: BoardViewPayload | null,
  settings: TableViewSettings,
  extraRows: Record<string, ItemRow[]>,
  canAddItem = true,
  subitems: SubitemsState = NO_SUBITEMS,
  options: { narrow?: boolean } = {},
): DerivedGrid {
  const columns = payload?.columns ?? [];
  const split = visibleColumns(columns, settings, NAME_COLUMN_ID);
  /**
   * §8.13 "Zoom", the whole of it (round 8f): below `NARROW_GRID_WIDTH` of **grid** width
   * *only the name column stays sticky*. `layout.ts#stickyWidth` already left the checkbox
   * gutter, the conversation cell **and `extraPinned`** out of the region's width there — but
   * the row went on rendering the pinned cells inside it, so a board with a pinned column
   * squeezed them into the leftover flex space: measured at 1 × 44 px each on a 390 px phone
   * (`cell-…-status`, `cell-…-date4`), which is a cell no one can read and the sweep cannot
   * click. A pinned column scrolls with the rest here instead, keeping its own width and its
   * place at the head of the scrolling columns, exactly as the rule reads.
   */
  const stickyNameOnly = options.narrow === true;
  const unpinned = stickyNameOnly ? split.pinned.filter((column) => column.id !== NAME_COLUMN_ID) : [];
  const pinned = stickyNameOnly ? split.pinned.filter((column) => column.id === NAME_COLUMN_ID) : split.pinned;
  const laidOut = [...unpinned, ...split.scrolling];
  /**
   * P-023: a type may declare that it is **not** a grid column. `subtasks` is the only one —
   * monday renders no `Subitems` header and no 105 px column; its affordance is the ⊕ in the
   * sticky region's conversation cell, and the column itself stays in `boards.columns` (it
   * carries `settings.boardIds`) and in *Hide*, which read `payload.columns`, not this list.
   */
  const scrolling = laidOut.filter((column) => getColumnUi(column.type).gridColumn !== false);
  const nameColumn = pinned.find((column) => column.id === NAME_COLUMN_ID);
  /**
   * P-021: `name.width` is the sticky region measured from the grid's left edge, so the cell
   * is what is left after the 6 px bar, the 33 px checkbox rail and the 65 px chat cell.
   */
  const nameStickyWidth = nameColumn ? columnWidth(nameColumn) : 0;
  const nameWidth = nameColumn ? nameCellWidth(nameStickyWidth) : 0;

  // The sticky region is laid out by CSS; only the horizontally scrolling columns get a
  // prefix sum (§6). Extra pinned columns render inside the sticky region, in order.
  const { layout, total } = layoutColumns(
    scrolling.map((column) => ({ id: column.id, width: columnWidth(column) })),
  );

  const rowsByGroup = new Map<string, ItemRow[]>();
  const itemsById = new Map<string, ItemRow>();
  for (const group of payload?.groups ?? []) {
    const rows = [...group.page.items, ...(extraRows[group.id] ?? [])];
    rowsByGroup.set(group.id, rows);
    for (const row of rows) itemsById.set(row.id, row);
  }

  // 6i / q455: a wrapped column grows every row to 56 px, exactly as monday does.
  const rowHeight = effectiveRowHeight(settings);
  /**
   * M3.S1: only rows the user has opened contribute a nested block, and a parent whose rows
   * have not arrived yet contributes an **empty** one — the header and `+ Add subitem` show
   * immediately, which is what monday does, and the arithmetic stays exact either way.
   */
  const expanded = new Set(subitems.expandedItemIds.map(String));
  const expandedSubitems = new Map<ItemId, readonly ItemId[]>();
  for (const itemId of expanded) {
    if (!itemsById.has(itemId)) continue;
    expandedSubitems.set(itemId as ItemId, (subitems.byParent[itemId] ?? []).map((row) => row.id));
  }
  /**
   * **P-308.** A parent whose subitems read was refused is collapsed (the store removes it from
   * `expandedItemIds`), so it contributes no block — only the muted line. A row that is somehow
   * both is treated as expanded: the rows arrived, which is the stronger fact.
   */
  const subitemsUnavailableFor = new Set<ItemId>(
    (subitems.unavailableFor ?? []).filter(
      (itemId) => itemsById.has(String(itemId)) && !expandedSubitems.has(itemId),
    ),
  );

  const flat = flatten(
    (payload?.groups ?? []).map((group) => ({
      id: group.id,
      collapsed: group.collapsed,
      itemIds: (rowsByGroup.get(group.id) ?? []).map((row) => row.id),
      canAddItem,
      expandedSubitems,
      subitemsUnavailable: subitemsUnavailableFor,
    })),
    rowHeight,
  );

  const geometry: GridGeometry = {
    rows: flat
      .filter((row) => row.kind === 'row')
      .map((row) => ({ itemId: row.itemId!, groupId: row.groupId })),
    columnIds: [...pinned, ...scrolling].map((column) => column.id),
  };

  return {
    pinned,
    scrolling,
    visible: [...pinned, ...scrolling],
    layout,
    scrollWidth: total,
    nameColumn,
    nameWidth,
    nameStickyWidth,
    rows: flat,
    tops: offsets(flat),
    height: totalHeight(flat),
    rowHeight,
    rowsByGroup,
    itemsById,
    geometry,
    itemOrder: geometry.rows.map((row) => row.itemId),
    subitemColumns: subitems.columns,
    subitemsByParent: subitems.byParent,
    subitemsUnavailableFor,
    subitemsError: subitems.error ?? null,
  };
}

/** The group a flat index belongs to, for the floating header. */
export function groupAtOffset(
  grid: DerivedGrid,
  scrollTop: number,
): { groupId: GroupId; index: number } | null {
  for (let index = grid.rows.length - 1; index >= 0; index -= 1) {
    if (grid.tops[index]! <= scrollTop) {
      return { groupId: grid.rows[index]!.groupId, index };
    }
  }
  const first = grid.rows[0];
  return first ? { groupId: first.groupId, index: 0 } : null;
}

/**
 * **P-328 — which groups the infinite-paging trigger pages** (§5.2, P7).
 *
 * The group the viewport's bottom edge is inside, as before; and, besides it, every group with
 * a cursor whose **last loaded row** is near the viewport — below `top - lookahead` and above
 * `bottom + lookahead`. The second rule is the fix: a 50-row page followed by another group put
 * the next group under the bottom edge, so the first one was never paged and its "+ Add item"
 * sat under row 50 for good. It is bounded on **both** sides: a group whose loaded end is far
 * above the viewport (a 9 326-row Done group scrolled past) must not be paged by every
 * re-render until all of it is fetched. A group with no cursor is never returned — it has
 * nothing left to page.
 */
export function groupsToPage(
  grid: DerivedGrid,
  top: number,
  bottom: number,
  cursors: Readonly<Record<string, string | null | undefined>>,
  lookahead: number,
): GroupId[] {
  const out: GroupId[] = [];
  const add = (groupId: GroupId) => {
    if (cursors[groupId] && !out.includes(groupId)) out.push(groupId);
  };
  const at = groupAtOffset(grid, bottom);
  if (at) add(at.groupId);
  const lastRowTop = new Map<GroupId, number>();
  grid.rows.forEach((row, index) => {
    if (row.kind === 'row') lastRowTop.set(row.groupId, grid.tops[index]!);
  });
  for (const [groupId, lastTop] of lastRowTop) {
    if (lastTop > top - lookahead && lastTop < bottom + lookahead) add(groupId);
  }
  return out;
}

/** Column widths keyed by id, for the header row and the footers. */
export function widthMap(columns: readonly BoardColumn[]): Map<ColumnId, number> {
  return new Map(columns.map((column) => [column.id, columnWidth(column)]));
}
